// =============================================
// Post-Validator Service
// v3.0: Clamp demand, validate slugs, fix hierarchy, enforce boundaries
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { checkBoundaryViolations, getBoundaries } from "./boundaryEnforcer";
import type { ThematicNode, SemanticBoundary } from "@/types/thematicMaps";

export interface PostValidationResult {
  clampedNodes: number;
  fixedSlugs: number;
  orphansFixed: number;
  boundaryViolations: number;
  needsReviewFlagged: number;
}

/**
 * Clamp demand indicators — override LLM-generated demand claims with real DataForSEO data.
 * If a node has demand_validated_at set, its data is authoritative.
 */
async function clampDemandIndicators(mapId: string): Promise<number> {
  // Nodes with validated data are authoritative — nothing to clamp
  // This function prevents LLM from overriding real demand data
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, demand_tier, demand_validated_at")
    .eq("map_id", mapId)
    .is("demand_validated_at", null)
    .not("demand_tier", "eq", "unknown");

  // Reset unvalidated nodes that somehow got a tier
  let count = 0;
  for (const node of nodes || []) {
    await (supabase as any)
      .from("thematic_nodes")
      .update({ demand_tier: "unknown", commercial_score: null })
      .eq("id", node.id);
    count++;
  }

  return count;
}

/**
 * Validate slug uniqueness per map.
 * If duplicates found, append -2, -3, etc.
 */
async function validateSlugs(mapId: string): Promise<number> {
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, slug")
    .eq("map_id", mapId)
    .order("created_at");

  if (!nodes) return 0;

  const slugCounts = new Map<string, number>();
  let fixed = 0;

  for (const node of nodes) {
    const count = slugCounts.get(node.slug) || 0;
    if (count > 0) {
      const newSlug = `${node.slug}-${count + 1}`;
      await (supabase as any)
        .from("thematic_nodes")
        .update({ slug: newSlug })
        .eq("id", node.id);
      fixed++;
    }
    slugCounts.set(node.slug, count + 1);
  }

  return fixed;
}

/**
 * Fix orphan nodes — nodes without a valid seed.
 * Attach them to the first seed in the map.
 */
async function validateHierarchy(mapId: string): Promise<number> {
  const { data: seeds } = await (supabase as any)
    .from("thematic_seeds")
    .select("id")
    .eq("map_id", mapId)
    .order("sort_order")
    .limit(1);

  if (!seeds || seeds.length === 0) return 0;

  const fallbackSeedId = seeds[0].id;

  // Find nodes whose seed_id doesn't match any seed in this map
  const { data: validSeeds } = await (supabase as any)
    .from("thematic_seeds")
    .select("id")
    .eq("map_id", mapId);

  const validSeedIds = new Set((validSeeds || []).map((s: { id: string }) => s.id));

  const { data: allNodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, seed_id")
    .eq("map_id", mapId);

  let fixed = 0;
  for (const node of allNodes || []) {
    if (!validSeedIds.has(node.seed_id)) {
      await (supabase as any)
        .from("thematic_nodes")
        .update({ seed_id: fallbackSeedId })
        .eq("id", node.id);
      fixed++;
    }
  }

  return fixed;
}

/**
 * Enforce boundary compliance — flag violating nodes with needs_review.
 */
async function enforceBoundaries(mapId: string): Promise<number> {
  const boundaries = await getBoundaries(mapId);
  if (!boundaries || (boundaries.not_owned.length === 0 && boundaries.adjacent.length === 0)) {
    return 0;
  }

  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("*")
    .eq("map_id", mapId);

  if (!nodes) return 0;

  const violations = checkBoundaryViolations(nodes as ThematicNode[], boundaries);

  // Flag violation nodes as needing review
  for (const v of violations.filter((v) => v.violationType === "not_owned")) {
    await (supabase as any)
      .from("thematic_nodes")
      .update({ node_status: "draft" })
      .eq("id", v.nodeId)
      .eq("node_status", "planned");
  }

  return violations.length;
}

/**
 * Flag AI-generated content without grounding data as needs_review.
 */
async function enforceNeedsReview(mapId: string): Promise<number> {
  // Nodes with content but no demand validation need review
  const { data: contentNodes } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, content_status")
    .eq("map_id", mapId)
    .neq("content_status", "empty");

  if (!contentNodes || contentNodes.length === 0) return 0;

  const nodeIds = contentNodes.map((c: { node_id: string }) => c.node_id);

  const { data: unvalidatedNodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id")
    .eq("map_id", mapId)
    .in("id", nodeIds)
    .is("demand_validated_at", null);

  return (unvalidatedNodes || []).length;
}

// =============================================
// Main post-validation pipeline
// =============================================

/**
 * Run full post-validation pipeline on a map.
 */
export async function postValidate(mapId: string): Promise<PostValidationResult> {
  const [clampedNodes, fixedSlugs, orphansFixed, boundaryViolations, needsReviewFlagged] =
    await Promise.all([
      clampDemandIndicators(mapId),
      validateSlugs(mapId),
      validateHierarchy(mapId),
      enforceBoundaries(mapId),
      enforceNeedsReview(mapId),
    ]);

  return {
    clampedNodes,
    fixedSlugs,
    orphansFixed,
    boundaryViolations,
    needsReviewFlagged,
  };
}
